import React from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import LineChartCard from 'src/components/cardLayouts/LineChartCard/LineChartCard';
import DashboardBarChartDiagramKpiCard from 'src/components/cardLayouts/DashboardBarChartDiagramKpiCard/DashboardBarChartDiagramKpiCard';
import { DashboardBarChartDiagramKpi } from 'src/services/kpi';
import styles from './CarrierCard.module.scss';
import Badge, { BadgeType } from '../Badge/Badge';
import Icon, { IconName } from '../Icon/Icon';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import Button, { ButtonToneProps } from '../Button/Button';
import Trend from '../Trend/Trend';
import Divider from '../Divider/Divider';

export interface CarrierCardProps {
  icon?: IconName;
  name: string;
  score?: number;
  badgeTitle?: string;
  detailsPath?: string;
  keyValuePairs?: {
    label: string;
    score: number | string;
    trend: number;
  }[];
  kpi?: {
    label: string;
    value: string;
    percentage: number;
  };
  chart?: DashboardBarChartDiagramKpi;
  summary?: {
    label: string;
    value: string;
  }[];
}

const CarrierCard: React.FC<CarrierCardProps> = ({
  icon,
  name,
  score,
  badgeTitle,
  detailsPath,
  keyValuePairs,
  kpi,
  chart,
  summary,
}) => {
  const navigate = useNavigate();

  return (
    <div className={classNames(styles.root)}>
      <div className="px-20 flex flex-col">
        <div className="flex justify-between relative">
          <div className={styles.iconBox}>
            <Icon name={icon ?? 'package'} width={28} height={28} />
          </div>
          <Button
            onClick={() => navigate(detailsPath ?? `/carrier-analytics/${name.toLowerCase()}`)}
            className="flex-shrink-0"
            title="View Details"
            tone={ButtonToneProps.SECONDARY}
          />
        </div>
        <div className="flex gap-x-8 mt-16 items-center flex-wrap">
          <h3 className="typo-text-16--bold text-full">{name}</h3>
          {score !== undefined && <ProgressIndicator rowLayout hideMaxIndicator percentage={score} />}
          {badgeTitle && <Badge type={BadgeType.Successful} expanded label={badgeTitle} />}
        </div>
      </div>
      <div className="px-20">
        <Divider customColor="border-subtle" className="my-16" />
        <div className="flex flex-col gap-y-8">
          {keyValuePairs?.map(info => (
            <div key={info.label} className="flex items-center gap-x-8">
              <span className="typo-text-14 text-faded flex-1">{info.label}</span>
              <span className="typo-text-14--medium text-full">{info.score}%</span>
              <Trend percentage={info.trend} />
            </div>
          ))}
        </div>
        {chart && (
          <div className="my-16">
            <DashboardBarChartDiagramKpiCard title={chart.title} data={chart} />
          </div>
        )}
        {!chart && kpi && (
          <div className="my-16">
            <LineChartCard
              title={kpi.label}
              kpi={{ smallLabel: true, percentage: kpi.percentage, value: Number(kpi.value) }}
            />
          </div>
        )}
        {summary && summary.length > 0 && (
          <>
            <Divider customColor="border-subtle" />
            <div className="mt-16 flex flex-col gap-y-8">
              {summary.map(info => (
                <div key={info.label} className="flex items-center gap-x-8">
                  <span className="typo-text-14 text-faded flex-1">{info.label}</span>
                  <span className="typo-text-14--bold text-full">{info.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CarrierCard;
